"use client";
import './i18n/autoTranslate';

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  CheckCircle,
  CreditCard,
  FileText,
  Lock,
  Mail,
  Scale,
  Shield,
  Sparkles,
  UserCheck,
  Zap,
} from 'lucide-react';
import Globe from '../components/lightswind/globe';
import SEO from './SEO';

const EFFECT = [0.22, 1, 0.36, 1];

const SectionWrapper = ({ children, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.75, ease: EFFECT }}
    viewport={{ once: true }}
    className={className}
  >
    {children}
  </motion.div>
);

const GlassCard = ({ children, className = '', hover = true }) => (
  <motion.div
    whileHover={hover ? { y: -6, transition: { duration: 0.25 } } : {}}
    className={`relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_0_40px_rgba(108,99,255,0.08)] transition-all duration-300 ${
      hover ? 'hover:border-white/20 hover:bg-white/[0.08] hover:shadow-[0_0_60px_rgba(108,99,255,0.15)]' : ''
    } ${className}`}
  >
    <img
      src="/assets/images/Gemini_Generated_6_dlmovh.png"
      className="absolute inset-0 -z-10 h-full w-full object-cover opacity-[0.07]"
      alt=""
    />
    {children}
  </motion.div>
);

const PolicySection = ({ icon: Icon, title, children }) => (
  <GlassCard className="p-6 md:p-8" hover={false}>
    <div className="flex flex-col gap-5 md:flex-row md:items-start">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-gradient-to-br from-white/10 to-white/[0.03]">
        <Icon className="h-6 w-6 text-[#8B7CFF]" />
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-2xl font-bold text-white md:text-3xl">{title}</h2>
        <div className="mt-4 space-y-4 text-base leading-8 text-gray-300">{children}</div>
      </div>
    </div>
  </GlassCard>
);

const BulletList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item) => (
      <li key={item} className="flex gap-3">
        <CheckCircle className="mt-1 h-4 w-4 shrink-0 text-[#8B7CFF]" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const TermsOfService = () => {
  const navigate = useNavigate();
  const lastUpdated = 'May 1, 2026';

  const quickPoints = [
    { icon: UserCheck, title: 'Use your real account information', text: 'Keep your login details secure and accurate.' },
    { icon: Bot, title: 'AI feedback is guidance', text: 'InterviewGenieAI helps you practice, but it cannot guarantee job offers.' },
    { icon: CreditCard, title: 'Plans and billing', text: 'Paid plans renew based on the billing terms shown at checkout.' },
    { icon: Shield, title: 'Respectful use', text: 'Do not misuse the platform, disrupt the service, or upload harmful content.' },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#07070A] text-white">
      <SEO
        title="Terms of Service — InterviewGenieAI"
        description="Read the terms that govern your use of InterviewGenieAI, including account rules, subscriptions, AI feedback, acceptable use, and limitations."
        canonical="https://interviewgenieai.com/terms-of-service"
      />

      <div className="pointer-events-none absolute inset-0 opacity-20">
        <Globe
          size="full"
          theta={0.2}
          dark={1}
          scale={1.1}
          diffuse={1.5}
          baseColor="#1a1a1a"
          markerColor="#6C63FF"
          glowColor="#444444"
          className="absolute"
        />
      </div>
      <div className="pointer-events-none absolute left-1/4 top-32 h-96 w-96 rounded-full bg-[#6C63FF]/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-20 right-1/4 h-96 w-96 rounded-full bg-[#8B7CFF]/10 blur-3xl" />

      <section className="relative z-10 px-4 pb-16 pt-32 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 inline-flex items-center gap-3"
          >
            <div className="h-px w-8 bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
            <span className="text-sm font-medium uppercase tracking-wider text-[#8B7CFF]">Legal Agreement</span>
            <div className="h-px w-8 bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-8 text-5xl font-bold tracking-tight md:text-6xl lg:text-7xl"
          >
            <span className="bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent">Terms of</span>
            <br />
            <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">Service</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mx-auto mb-6 max-w-3xl text-xl leading-relaxed text-gray-300"
          >
            These terms explain how you may use InterviewGenieAI, what you can expect from us, and the responsibilities that keep the platform safe and useful for everyone.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-sm font-medium uppercase tracking-wider text-gray-500"
          >
            Last updated: {lastUpdated}
          </motion.p>
        </div>
      </section>

      <section className="relative z-10 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 lg:grid-cols-4">
          {quickPoints.map((point, index) => {
            const Icon = point.icon;
            return (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: index * 0.08 }}
                viewport={{ once: true }}
              >
                <GlassCard className="h-full p-6">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/[0.04]">
                    <Icon className="h-6 w-6 text-[#8B7CFF]" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{point.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-gray-400">{point.text}</p>
                </GlassCard>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="relative z-10 px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl space-y-8">
          <SectionWrapper>
            <PolicySection icon={FileText} title="1. Acceptance of these Terms">
              <p>
                By creating an account, signing in, subscribing, or using InterviewGenieAI, you agree to these Terms of Service and any policies linked from this page. If you do not agree, please do not use the platform.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Sparkles} title="2. What InterviewGenieAI provides">
              <p>
                InterviewGenieAI is an AI-powered interview preparation platform. It may provide practice interviews, AI-generated questions, answer feedback, scoring, analytics, resume-related suggestions, learning resources, and progress tracking.
              </p>
              <p>
                The platform is designed for preparation and learning. It does not replace professional career advice, employer feedback, legal advice, or a real hiring decision.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={UserCheck} title="3. Accounts and security">
              <BulletList
                items={[
                  'You are responsible for keeping your account credentials secure.',
                  'You should provide accurate account and billing information.',
                  'You must not share, sell, or transfer your account without our permission.',
                  'You should contact us if you believe your account has been accessed without permission.',
                ]}
              />
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Bot} title="4. AI-generated content and feedback">
              <p>
                AI outputs can be helpful, but they may sometimes be incomplete, inaccurate, or not suitable for your exact situation. You are responsible for reviewing AI suggestions before relying on them.
              </p>
              <BulletList
                items={[
                  'AI feedback is intended for interview practice and self-improvement.',
                  'Scores and recommendations are estimates, not guarantees of hiring success.',
                  'You should not submit confidential employer materials or private third-party information unless you have permission.',
                ]}
              />
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Lock} title="5. Acceptable use">
              <p>You agree not to misuse InterviewGenieAI or interfere with the platform. This includes:</p>
              <BulletList
                items={[
                  'Attempting to reverse engineer, scrape, overload, or disrupt the service.',
                  'Uploading malware, harmful code, illegal content, or content that violates another person’s rights.',
                  'Using the platform to impersonate others or misrepresent your qualifications.',
                  'Sharing content that is abusive, discriminatory, harassing, or otherwise harmful.',
                ]}
              />
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={CreditCard} title="6. Subscriptions, billing, and cancellations">
              <p>
                Some features may require a paid subscription. Prices, usage limits, included features, renewal dates, and billing intervals are shown at checkout or inside your account billing area.
              </p>
              <BulletList
                items={[
                  'Subscriptions may renew automatically unless canceled before the next billing date.',
                  'You can manage or cancel your plan from the billing section when available.',
                  'If a payment fails, paid features may be limited or your account may be moved to a free plan.',
                  'Refunds are handled according to the plan terms shown at purchase and any applicable consumer protection rules.',
                ]}
              />
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Shield} title="7. Your content and platform rights">
              <p>
                You keep ownership of the resumes, answers, text, and other materials you submit. You give InterviewGenieAI permission to process that content only as needed to provide, secure, improve, and support the service.
              </p>
              <p>
                InterviewGenieAI, including its design, software, branding, workflows, and platform content, belongs to InterviewGenieAI or its licensors. You may not copy or reuse it except as allowed by these terms.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={AlertTriangle} title="8. Availability and limitations">
              <p>
                We work to keep InterviewGenieAI reliable, but we do not guarantee uninterrupted access or error-free results. Features may change as we improve the product.
              </p>
              <p>
                To the maximum extent allowed by law, InterviewGenieAI is provided “as is” and we are not responsible for indirect, incidental, special, or consequential damages related to your use of the platform.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Scale} title="9. Changes to these Terms">
              <p>
                We may update these Terms when the platform, laws, or business requirements change. If changes are important, we will take reasonable steps to notify users, such as updating this page or showing an in-product notice.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Mail} title="10. Contact">
              <p>
                For questions about these Terms, contact us at{' '}
                <a href="mailto:support@interviewgenieai.com" className="text-[#8B7CFF] hover:text-white">
                  support@interviewgenieai.com
                </a>
                .
              </p>
            </PolicySection>
          </SectionWrapper>
        </div>
      </section>

      <section className="relative z-10 px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <SectionWrapper>
            <GlassCard className="p-10 text-center md:p-12">
              <h2 className="text-3xl font-bold text-white md:text-5xl">Ready to practice with confidence?</h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-gray-300">
                Start with a free AI interview session and improve your answers with structured, actionable feedback.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
                <button
                  onClick={() => navigate('/pricing')}
                  className="inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] px-8 py-4 font-semibold text-white transition-all duration-300 hover:shadow-[0_0_50px_rgba(108,99,255,0.45)]"
                >
                  <Zap className="h-5 w-5" />
                  View Plans
                  <ArrowRight className="h-5 w-5" />
                </button>
                <Link
                  to="/privacy-policy"
                  className="inline-flex items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/5 px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-white/10"
                >
                  Privacy Policy
                </Link>
              </div>
            </GlassCard>
          </SectionWrapper>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10 bg-black/30 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 md:flex-row">
          <div className="flex items-center gap-3">
            <img src="/GenieAI_2.png" alt="InterviewGenieAI" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-bold text-white">InterviewGenieAI</div>
              <div className="text-sm text-gray-500">AI interview preparation platform</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <Link to="/about" className="text-gray-400 transition-colors hover:text-[#8B7CFF]">About</Link>
            <Link to="/privacy-policy" className="text-gray-400 transition-colors hover:text-[#8B7CFF]">Privacy</Link>
            <Link to="/terms-of-service" className="text-gray-400 transition-colors hover:text-[#8B7CFF]">Terms</Link>
            <Link to="/contact" className="text-gray-400 transition-colors hover:text-[#8B7CFF]">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
